import { useAccount, useMsal } from "@azure/msal-react";
import {
    ActionIcon,
    Button,
    Card,
    Container,
    Divider,
    Group,
    Modal,
    Select,
    Stack,
    Switch,
    Text,
    Textarea,
    TextInput,
    Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconCircleCheck, IconClock, IconInfoCircle, IconKey, IconLock } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useLocation, useNavigate } from "@tanstack/react-router";
import { useLocalStorage } from "@uidotdev/usehooks";
import { useEffect, useMemo, useRef, useState } from "react";
import { DatasourceFormattedText } from "src/components/DatasourceFormattedText";
import DatasourceRequestAccessModal from "src/components/DatasourceRequestAccessModal";
import { isNonRequestableAccessPolicy } from "src/db";
import { useOrganizations } from "src/hooks/useConfiguration";
import {
    isRoleGranted,
    type UserDatasourceDetail,
    useSyncUserAccess,
    useUser,
    useUserDatasources,
} from "src/hooks/useUser";
import { postAPI } from "src/utils/fetch";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

interface ProductLineOption {
    value: string;
    label: string;
}

interface DatasourceChipsProps {
    dataSources: UserDatasourceDetail[];
    selectedDataSources: Record<string, boolean>;
    onDataSourceToggle: (dataSourceId: string, selected: boolean) => void;
    requesting_access: boolean;
    pendingRequests: Set<string>;
    setPendingRequests: React.Dispatch<React.SetStateAction<Set<string>>>;
    /** When true, every accessible knowledge base is treated as selected (empty prefs = all). */
    useAllDatasources?: boolean;
}

// A datasource can be requested when the user doesn't have it yet, hasn't already
// asked for it, and it could be granted at all. Synced and admin-assigned datasources
// can never be granted through this flow, so they never qualify.
function isRequestableDatasource(
    dataSource: UserDatasourceDetail | undefined,
    pendingRequests: Set<string>,
) {
    return (
        !!dataSource &&
        dataSource.has_access === false &&
        !isNonRequestableAccessPolicy(dataSource.access_policy) &&
        !pendingRequests.has(dataSource.id)
    );
}

// A chip is selectable when the user already has access (preferences), or - while
// composing an access request - when it is still requestable. Granted, pending,
// synced, and admin-assigned datasources stay locked in requesting_access mode
// and only show their status.
function isSelectableDatasource(
    dataSource: UserDatasourceDetail,
    requesting_access: boolean,
    pendingRequests: Set<string>,
) {
    if (requesting_access) return isRequestableDatasource(dataSource, pendingRequests);
    return dataSource.has_access !== false;
}

function DatasourceChips({
    dataSources,
    selectedDataSources,
    onDataSourceToggle,
    requesting_access,
    pendingRequests,
    setPendingRequests,
    useAllDatasources = false,
}: DatasourceChipsProps) {
    const [accessRequestModalOpened, { open: openAccessRequest, close: closeAccessRequest }] =
        useDisclosure(false);
    const [selectedDatasource, setSelectedDatasource] = useState<UserDatasourceDetail | null>(null);
    const [infoModalOpened, { open: openInfoModal, close: closeInfoModal }] = useDisclosure(false);
    const [infoDatasource, setInfoDatasource] = useState<UserDatasourceDetail | null>(null);

    if (dataSources.length === 0) {
        return (
            <Text size="sm" c="dimmed">
                No knowledge bases match the selected filters.
            </Text>
        );
    }

    // Split the list so selectable knowledge bases sit above a divider and the locked
    // ones (pending, not requested, synced - or, when requesting, already granted) sit
    // below it.
    const accessibleDataSources = dataSources.filter((ds) =>
        isSelectableDatasource(ds, requesting_access, pendingRequests),
    );
    const restrictedDataSources = dataSources.filter(
        (ds) => !isSelectableDatasource(ds, requesting_access, pendingRequests),
    );

    const renderChip = (dataSource: UserDatasourceDetail) => {
        const isSelectable = isSelectableDatasource(dataSource, requesting_access, pendingRequests);
        const isSelected =
            (useAllDatasources && isSelectable && !requesting_access) ||
            selectedDataSources[dataSource.id] === true;

        let iconElement = null;
        let tooltipLabel = "";
        let chipClickHandler: (() => void) | null = null;

        if (isSelectable && requesting_access) {
            iconElement = <IconKey size="0.875rem" color={isSelected ? "white" : "blue"} />;
            tooltipLabel = "Select to request access";
            chipClickHandler = () => {
                onDataSourceToggle(dataSource.id, !isSelected);
            };
        } else if (isSelectable) {
            iconElement = (
                <IconCircleCheck size="0.875rem" color={isSelected ? "white" : "green"} />
            );
            tooltipLabel = useAllDatasources
                ? "Using all available - click to pick specific knowledge bases"
                : "Access granted";
            chipClickHandler = () => {
                onDataSourceToggle(dataSource.id, !isSelected);
            };
        } else if (isNonRequestableAccessPolicy(dataSource.access_policy)) {
            // Access is not requestable: synced (external process) or
            // admin-assigned (an admin must grant the role).
            iconElement = <IconLock size="0.875rem" color="gray" />;
            tooltipLabel = "Access is managed automatically - contact your administrator";
        } else if (pendingRequests.has(dataSource.id)) {
            iconElement = <IconClock size="0.875rem" color="#f59e0b" />;
            tooltipLabel = "Access request pending";
        } else if (dataSource.has_access !== false) {
            // Only reachable while requesting access: already granted, nothing to ask for.
            iconElement = <IconCircleCheck size="0.875rem" color="green" />;
            tooltipLabel = "Access granted";
        } else {
            iconElement = <IconKey size="0.875rem" color="blue" />;
            tooltipLabel = "Request access";
            chipClickHandler = () => {
                setSelectedDatasource(dataSource);
                openAccessRequest();
            };
        }

        return (
            <Tooltip key={dataSource.id} label={tooltipLabel}>
                <Button
                    onClick={chipClickHandler ?? undefined}
                    size="xs"
                    disabled={chipClickHandler === null}
                    radius="sm"
                    variant="filled"
                    color={isSelected ? "violet" : "gray.2"}
                    styles={{
                        root: {
                            cursor: chipClickHandler ? "pointer" : "not-allowed",
                            opacity: isSelectable ? 1 : 0.8,
                            height: "24px",
                            padding: "4px 8px",
                        },
                        inner: {
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            fontWeight: isSelected ? 600 : 400,
                            fontSize: "12px",
                            color: isSelected ? "white" : "#666",
                        },
                    }}
                >
                    {iconElement}
                    <span style={{ marginLeft: 6 }}>{dataSource.name}</span>
                    <Tooltip label="View description and ingestion status">
                        <ActionIcon
                            component="span"
                            aria-label={`View details for ${dataSource.name}`}
                            size="xs"
                            variant="transparent"
                            color={isSelected ? "white" : "gray"}
                            style={{ marginLeft: 6 }}
                            onClick={(event) => {
                                event.stopPropagation();
                                setInfoDatasource(dataSource);
                                openInfoModal();
                            }}
                        >
                            <IconInfoCircle size={14} />
                        </ActionIcon>
                    </Tooltip>
                </Button>
            </Tooltip>
        );
    };

    return (
        <Stack gap={6}>
            {accessibleDataSources.length > 0 && (
                <Group gap={8} align="center" data-testid="accessible-knowledge-bases">
                    {accessibleDataSources.map(renderChip)}
                </Group>
            )}

            {accessibleDataSources.length > 0 && restrictedDataSources.length > 0 && (
                <Divider
                    my={4}
                    label={
                        requesting_access
                            ? "Already granted, pending, or managed externally"
                            : "Not yet accessible - click to request access"
                    }
                    labelPosition="left"
                />
            )}

            {restrictedDataSources.length > 0 && (
                <Group gap={8} align="center" data-testid="restricted-knowledge-bases">
                    {restrictedDataSources.map(renderChip)}
                </Group>
            )}

            {infoDatasource && (
                <Modal
                    opened={infoModalOpened}
                    onClose={closeInfoModal}
                    title={infoDatasource.name}
                    size="lg"
                    closeButtonProps={{ "aria-label": "Close modal" }}
                >
                    <Stack>
                        {infoDatasource.description && (
                            <DatasourceFormattedText
                                label="Description"
                                value={infoDatasource.description}
                            />
                        )}
                        {infoDatasource.ingestion_status && (
                            <DatasourceFormattedText
                                label="Ingestion Status"
                                value={infoDatasource.ingestion_status}
                            />
                        )}
                        {!infoDatasource.description && !infoDatasource.ingestion_status && (
                            <Text size="sm" c="dimmed">
                                No additional details available for this knowledge base.
                            </Text>
                        )}
                    </Stack>
                </Modal>
            )}

            {selectedDatasource && (
                <DatasourceRequestAccessModal
                    opened={accessRequestModalOpened}
                    onClose={() => {
                        closeAccessRequest();
                    }}
                    onSubmit={() => {
                        setPendingRequests((prev) => new Set([...prev, selectedDatasource.id]));
                    }}
                    title={`Request Access to ${selectedDatasource.name}`}
                    description={selectedDatasource.description}
                    ingestion_status={selectedDatasource.ingestion_status}
                    requested_datasources={[selectedDatasource.id]}
                />
            )}

            {/* Preferences use the "Use all available" switch instead; access requests keep
                quick All/None shortcuts since a product line pre-selects every requestable one. */}
            {requesting_access && dataSources.length > 1 && (
                <Group gap={3} ml={4}>
                    <Text
                        fw={11}
                        c="dimmed"
                        style={{
                            cursor: "pointer",
                            fontWeight: 500,
                            opacity: 0.7,
                        }}
                        onClick={() => {
                            for (const ds of accessibleDataSources) {
                                onDataSourceToggle(ds.id, true);
                            }
                        }}
                    >
                        All
                    </Text>
                    <Text fw={11} c="dimmed" style={{ opacity: 0.5 }}>
                        ·
                    </Text>
                    <Text
                        fw={11}
                        c="dimmed"
                        style={{
                            cursor: "pointer",
                            fontWeight: 500,
                            opacity: 0.7,
                        }}
                        onClick={() => {
                            for (const ds of dataSources) {
                                onDataSourceToggle(ds.id, false);
                            }
                        }}
                    >
                        None
                    </Text>
                </Group>
            )}
        </Stack>
    );
}

interface QuestionnaireProps {
    heading_text?: string;
    paragraph_text?: string;
    button_text?: string;
    requesting_access?: boolean;
    // Set when rendered inside a modal (unauthorized page): Cancel and a successful
    // access request close the modal instead of navigating away / reloading.
    onClose?: () => void;
}

function Questionnaire({
    heading_text = "Would you like to filter the search results by product line and knowledge bases?",
    paragraph_text = `
    This is an optional filter to narrow down the search results, based on the product line and knowledge bases you are interested in.
    You can request access to the knowledge bases that you don't have access to just by clicking on the knowledge base.
    `,
    button_text = "Save Preferences",
    requesting_access = false,
    onClose,
}: QuestionnaireProps = {}) {
    const TRAINING_URL =
        "https://siemens-energy-learning.sabacloud.com/Saba/Web_spf/EU5P1PRD001/app/me/learningeventdetail/cours000000000184778)";

    const [selectedProductLine, setSelectedProductLine] = useState<string>("all");
    const [knowledgeBaseSearchTerm, setKnowledgeBaseSearchTerm] = useState("");
    const [dataSourceSelections, setDataSourceSelections] = useState<Record<string, boolean>>({});
    // Empty preferences.datasources means "use every datasource the user can access"
    // (backend treats it as no filter). Default on so new grants are included automatically.
    const [useAllDatasources, setUseAllDatasources] = useState(true);
    const [loading, setLoading] = useState(false);
    const [productLineDataSources, setProductLineDataSources] = useState<
        Record<string, Set<string>>
    >({});
    const [productLineOptions, setProductLineOptions] = useState<ProductLineOption[]>([]);
    const navigate = useNavigate();
    // Refresh on open (unless fetched within USER_STALE_TIME_MS) so permissions/
    // preferences/pending access requests reflect the latest server state rather
    // than a stale cached /v1/user response from an earlier page.
    const { data: user, isFetching: isUserFetching } = useUser({
        refreshOnMount: true,
    });
    const location = useLocation();
    const { source, requesting_access: requestingAccessParam } = location.search as {
        source?: string;
        requesting_access?: string;
    };
    const account = useAccount();
    const isExternalUser = user?.info?.roles?.includes("external") ?? false;

    const isRequestingAccess = requestingAccessParam === "true" || requesting_access;

    // External users skip datasource selection and go straight to chat
    useEffect(() => {
        if (isExternalUser && !isRequestingAccess) {
            navigate({ to: "/" });
        }
    }, [isExternalUser, isRequestingAccess, navigate]);

    // Compute display text based on isRequestingAccess mode
    const environment = import.meta.env.VITE_ENVIRONMENT;
    const displayHeadingText =
        isRequestingAccess && !requesting_access
            ? `Would you like to request access to Knowledge Search (${environment})?`
            : heading_text;
    const displayParagraphText =
        isRequestingAccess && !requesting_access
            ? "Please specify the knowledge bases that you need access to, an admin will review your request."
            : paragraph_text;
    const displayButtonText =
        isRequestingAccess && !requesting_access ? "Request Access" : button_text;

    const initialized = useRef(false);
    const productLineInitialized = useRef(false);

    const { data: userDatasourceDetails } = useUserDatasources();
    const { data: organizations } = useOrganizations();
    const [userDatasources, setUserDatasources] = useLocalStorage<UserDatasourceDetail[]>(
        "datasources",
        [],
    );

    const [chatPreferences, setChatPreferences] = useLocalStorage<{
        selectedProductLines: string[];
        selectedDatasources: string[];
    }>("chatPreferences", {
        selectedProductLines: [],
        selectedDatasources: [],
    });

    const [, setSelectedOrganization] = useLocalStorage<string>("selectedOrganization", "");

    const [, setSubmitting] = useState(false);
    const { instance } = useMsal();
    const queryClient = useQueryClient();
    const syncUserAccess = useSyncUserAccess();

    const [requestReason, setRequestReason] = useState("");
    const [pendingRequests, setPendingRequests] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (initialized.current) return;
        if (isUserFetching) return;
        if (!user || !userDatasourceDetails || !organizations) return;
        // Sync from backend whenever preferences were previously saved locally, or
        // the backend has an explicit datasource list. Empty datasources = use all.
        const backendDatasources = user.info.user_preferences.datasources ?? [];
        let hasLocalPreferences = false;
        try {
            const stored = localStorage.getItem("chatPreferences");
            if (stored) {
                const parsed = JSON.parse(stored) as { selectedProductLines?: string[] };
                hasLocalPreferences = (parsed.selectedProductLines?.length ?? 0) > 0;
            }
        } catch {
            hasLocalPreferences = false;
        }
        if (backendDatasources.length > 0 || hasLocalPreferences) {
            localStorage.setItem(
                "memoryEnabled",
                String(user.info.user_preferences.memory_enabled),
            );
            setChatPreferences({
                selectedProductLines: [user.info.user_preferences.product_line || "all"],
                selectedDatasources: backendDatasources,
            });
        }
        initialized.current = true;
        setUserDatasources(userDatasourceDetails);

        const productLineToDataSources: Record<string, Set<string>> = {};
        productLineToDataSources.all = new Set(userDatasourceDetails.map((ds) => ds.id));

        for (const org of organizations) {
            for (const productLine of org.product_lines) {
                productLineToDataSources[productLine] = new Set(
                    userDatasourceDetails
                        .filter((ds) => ds.product_line_filter.includes(productLine))
                        .map((ds) => ds.id),
                );
            }
        }

        setProductLineDataSources(productLineToDataSources);

        const orgOrder = [
            "GS D AGT",
            "GS D MGT",
            "GS D SGT",
            "GS C",
            "TI CP (RCE & TCP)",
            "TI STG",
        ];

        const options: ProductLineOption[] = [];
        for (const orgId of orgOrder) {
            const org = organizations.find((o) => o.id === orgId);
            if (org && org.product_lines.length > 0) {
                const productLine = org.product_lines[org.product_lines.length - 1];
                options.push({
                    value: productLine,
                    label: org.description || productLine,
                });
            }
        }

        setProductLineOptions(options);
    }, [
        user,
        userDatasourceDetails,
        organizations,
        isUserFetching,
        setUserDatasources,
        setChatPreferences,
    ]);

    useEffect(() => {
        if (productLineInitialized.current) return;
        if (!chatPreferences || Object.keys(productLineDataSources).length === 0) return;

        productLineInitialized.current = true;

        const initialProductLine =
            chatPreferences.selectedProductLines?.length > 0
                ? chatPreferences.selectedProductLines[0]
                : "all";

        setSelectedProductLine(initialProductLine);

        const initialSelections: Record<string, boolean> = {};
        const availableIds = productLineDataSources[initialProductLine];
        const hasExplicitDatasources = (chatPreferences.selectedDatasources?.length ?? 0) > 0;

        // Empty selectedDatasources means "use all available" (backend filter no-op),
        // not "none selected" — unless we're composing an access request.
        if (!isRequestingAccess) {
            setUseAllDatasources(!hasExplicitDatasources);
        }

        if (availableIds) {
            if (hasExplicitDatasources) {
                for (const dsId of chatPreferences.selectedDatasources) {
                    const datasource = user?.datasources?.find((ds) => ds.id === dsId);
                    if (availableIds.has(dsId) && isRoleGranted(datasource)) {
                        initialSelections[dsId] = true;
                    }
                }

                if (Object.keys(initialSelections).length === 0) {
                    for (const dsId of availableIds) {
                        const datasource = user?.datasources?.find((ds) => ds.id === dsId);
                        initialSelections[dsId] = isRoleGranted(datasource);
                    }
                    if (!isRequestingAccess) {
                        setUseAllDatasources(true);
                    }
                }
            } else {
                for (const dsId of availableIds) {
                    const datasource = user?.datasources?.find((ds) => ds.id === dsId);
                    initialSelections[dsId] = isRoleGranted(datasource);
                }
            }
        }
        setDataSourceSelections(initialSelections);
    }, [chatPreferences, productLineDataSources, user, isRequestingAccess]);

    const filteredDataSources = useMemo(() => {
        const dataSources =
            !selectedProductLine || selectedProductLine === "all"
                ? userDatasources
                : userDatasources.filter((ds) =>
                      (ds.product_line_filter ?? []).includes(selectedProductLine),
                  );
        return dataSources.toSorted((a, b) => a.name.localeCompare(b.name));
    }, [userDatasources, selectedProductLine]);

    const searchedDataSources = useMemo(() => {
        const term = knowledgeBaseSearchTerm.trim().toLowerCase();
        if (!term) return filteredDataSources;
        return filteredDataSources.filter((ds) => ds.name.toLowerCase().includes(term));
    }, [filteredDataSources, knowledgeBaseSearchTerm]);

    const handleProductLineFilterChange = (value: string | null) => {
        const nextProductLine = value || "all";
        setSelectedProductLine(nextProductLine);
        setKnowledgeBaseSearchTerm("");

        const availableIds =
            productLineDataSources[nextProductLine] ?? new Set(userDatasources.map((ds) => ds.id));

        // Picking a product line pre-selects everything usable in it: granted knowledge
        // bases for preferences, still-requestable ones when composing an access request.
        // In "use all" mode the chips are display-only — empty prefs stay empty on save.
        if (!isRequestingAccess && useAllDatasources) {
            return;
        }

        const freshSelections: Record<string, boolean> = {};
        for (const dsId of availableIds) {
            freshSelections[dsId] = isRequestingAccess
                ? isRequestableDatasource(
                      userDatasources.find((ds) => ds.id === dsId),
                      pendingRequests,
                  )
                : isRoleGranted(user?.datasources?.find((ds) => ds.id === dsId));
        }
        setDataSourceSelections(freshSelections);
    };

    const handleDataSourceToggle = (dataSourceId: string, isChecked: boolean) => {
        if (useAllDatasources && !isRequestingAccess) {
            // Leaving "use all" — materialize every granted datasource, then apply the click.
            setUseAllDatasources(false);
            const next: Record<string, boolean> = {};
            for (const ds of userDatasources) {
                if (!isRoleGranted(user?.datasources?.find((d) => d.id === ds.id))) continue;
                next[ds.id] = ds.id === dataSourceId ? isChecked : true;
            }
            setDataSourceSelections(next);
            return;
        }
        setDataSourceSelections((prev) => ({
            ...prev,
            [dataSourceId]: isChecked,
        }));
    };

    const handleUseAllDatasourcesChange = (enabled: boolean) => {
        setUseAllDatasources(enabled);
        if (!enabled) {
            // Turning off "use all" clears every chip so Save stays disabled until the
            // user explicitly picks at least one knowledge base.
            const next: Record<string, boolean> = {};
            for (const ds of userDatasources) {
                next[ds.id] = false;
            }
            setDataSourceSelections(next);
        }
    };

    const handleRequestAccess = async () => {
        try {
            if (!account || !requestReason.trim()) return;
            // Drop anything that can no longer be requested (granted / pending / locked)
            // in case a stale selection survived a status refresh.
            const selectedDataSources = Object.keys(dataSourceSelections).filter(
                (key) =>
                    dataSourceSelections[key] === true &&
                    isRequestableDatasource(
                        userDatasources.find((ds) => ds.id === key),
                        pendingRequests,
                    ),
            );
            if (selectedDataSources.length === 0) return;
            // Decide before the requests land, otherwise the refetched user already
            // carries the ones we are about to create.
            const isFirstRequest = (user?.access_requests?.length ?? 0) === 0;

            setSubmitting(true);
            setLoading(true);
            // One request carries every selected knowledge base; the admin reviews it as
            // a whole and grants whatever roles fit.
            const response = await postAPI({
                url: `${BACKEND_URL}/v1/user/access-requests`,
                payload: {
                    user_id:
                        account.idTokenClaims?.gid ||
                        account.idTokenClaims?.SCGID ||
                        account.idTokenClaims?.siemens_gid ||
                        account.localAccountId,
                    user_email: account.username,
                    requested_datasources: selectedDataSources,
                    reason: requestReason,
                },
                instance,
            });
            // Re-sync permissions after every request - a rejected one often means the
            // cached access is out of date (already granted / already pending).
            await syncUserAccess();
            if (response.status >= 300) {
                throw new Error(
                    "Failed to request access, Please try again later or contact our product owner",
                );
            }
            notifications.show({
                title: "Saved",
                message:
                    "We'd review your request as soon as possible and get back to you, thanks for requesting access.",
            });

            // First-time requesters are pointed to the onboarding training video; anyone
            // who already has a request on file has been there.
            if (isFirstRequest) {
                const newWindow = window.open(TRAINING_URL, "_blank", "noopener,noreferrer");
                if (newWindow) newWindow.opener = null;
            }

            setPendingRequests((prev) => new Set([...prev, ...selectedDataSources]));
            if (onClose) {
                onClose();
            } else {
                setTimeout(() => window.location.reload(), 2000);
            }
        } catch (error: unknown) {
            const err = error as {
                message?: string;
                response?: { data?: { error?: { message?: string } } };
            };
            notifications.show({
                title: "Error",
                color: "red",
                message:
                    err.message ??
                    err.response?.data?.error?.message ??
                    "Error submitting request.",
            });
        } finally {
            setSubmitting(false);
            setLoading(false);
        }
    };

    const handleSavePreferences = async () => {
        // Empty array = use all available datasources (backend applies no datasource filter).
        const selectedDataSources = useAllDatasources
            ? []
            : Object.keys(dataSourceSelections).filter((key) => dataSourceSelections[key] === true);

        let selectedOrgName = "";
        if (selectedProductLine && selectedProductLine !== "all") {
            for (const org of organizations || []) {
                if (org.product_lines.includes(selectedProductLine)) {
                    selectedOrgName = org.id;
                    break;
                }
            }
        }

        const storedMemoryPreference = localStorage.getItem("memoryEnabled");
        const memoryPreference = storedMemoryPreference ? storedMemoryPreference === "true" : false;

        setLoading(true);

        try {
            await postAPI({
                url: `${BACKEND_URL}/v1/user/preferences`,
                payload: {
                    product_line: selectedProductLine.toUpperCase(),
                    datasources: selectedDataSources,
                    memory_enabled: memoryPreference,
                },
                instance,
                expectedStatus: 200,
            });

            setChatPreferences({
                selectedProductLines: selectedProductLine ? [selectedProductLine] : [],
                selectedDatasources: selectedDataSources,
            });

            setSelectedOrganization(selectedOrgName);

            if (window.opener) {
                window.opener.postMessage({ type: "cts-preferences-updated" }, "*");
            }

            setTimeout(async () => {
                await queryClient.invalidateQueries({ queryKey: ["user"] });
                if (source) {
                    await navigate({ to: source });
                } else {
                    await navigate({ to: "/" });
                }
                setLoading(false);
            }, 100);
        } catch (error) {
            console.error("Error updating user preferences: ", error);
            notifications.show({
                title: "Error",
                color: "red",
                message: "Failed to save preferences. Please try again.",
            });
            setLoading(false);
        }
    };

    // Derived from the /v1/user payload so no separate access-requests call is
    // needed - the user endpoint already returns pending requests.
    useEffect(() => {
        const datasourceIds = new Set<string>();
        for (const req of user?.access_requests ?? []) {
            for (const ds of req.requested_datasources ?? []) {
                datasourceIds.add(ds.id);
            }
        }
        setPendingRequests(datasourceIds);
    }, [user]);

    const visibleSelections = useMemo(() => {
        const visibleIds = new Set(searchedDataSources.map((ds) => ds.id));
        const result: Record<string, boolean> = {};
        for (const [id, selected] of Object.entries(dataSourceSelections)) {
            if (visibleIds.has(id)) {
                result[id] = selected;
            }
        }
        return result;
    }, [dataSourceSelections, searchedDataSources]);

    return (
        <main className="mx-auto p-4">
            <Container size="md">
                <div>
                    <Text size="xl" fw={600}>
                        {displayHeadingText}
                    </Text>
                    <Text size="sm" c="dimmed" mt={24} mb={24} style={{ whiteSpace: "pre-line" }}>
                        {displayParagraphText}
                    </Text>

                    <Stack gap="md">
                        <Group align="center" gap="xs" wrap="nowrap" w="100%">
                            <Text size="sm" style={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                                Filter by Product Line:
                            </Text>
                            <Select
                                aria-label="Filter by Product Line"
                                placeholder="No selection"
                                data={[
                                    { value: "all", label: "No selection" },
                                    ...productLineOptions,
                                ]}
                                value={selectedProductLine}
                                onChange={handleProductLineFilterChange}
                                searchable
                                clearable
                                nothingFoundMessage="No product line found"
                                clearButtonProps={{ "aria-label": "Clear product line filter" }}
                                style={{ flex: 1 }}
                            />
                        </Group>

                        <Card shadow="sm" p="lg" radius="md" withBorder w="100%">
                            <Group justify="space-between" align="center" mb="sm" wrap="nowrap">
                                <Text size="sm" fw={600}>
                                    Knowledge Bases
                                </Text>
                                {!isRequestingAccess && (
                                    <Tooltip
                                        label="Include every knowledge base you can access, including ones granted later"
                                        multiline
                                        w={280}
                                        withArrow
                                    >
                                        <span>
                                            <Switch
                                                label="Use all available"
                                                checked={useAllDatasources}
                                                onChange={(event) =>
                                                    handleUseAllDatasourcesChange(
                                                        event.currentTarget.checked,
                                                    )
                                                }
                                                size="sm"
                                                color="teal"
                                                styles={{ body: { alignItems: "center" } }}
                                            />
                                        </span>
                                    </Tooltip>
                                )}
                            </Group>
                            <TextInput
                                aria-label="Filter knowledge bases"
                                placeholder="Filter knowledge bases"
                                value={knowledgeBaseSearchTerm}
                                onChange={(event) =>
                                    setKnowledgeBaseSearchTerm(event.currentTarget.value)
                                }
                                mb="sm"
                            />
                            <DatasourceChips
                                dataSources={searchedDataSources}
                                selectedDataSources={visibleSelections}
                                onDataSourceToggle={handleDataSourceToggle}
                                requesting_access={isRequestingAccess}
                                pendingRequests={pendingRequests}
                                setPendingRequests={setPendingRequests}
                                useAllDatasources={!isRequestingAccess && useAllDatasources}
                            />
                        </Card>

                        <Textarea
                            required
                            label="Reason for access:"
                            placeholder="Example: I need it to help me with my daily customer support work..."
                            style={{ flex: 1 }}
                            size="xl"
                            value={requestReason}
                            onChange={(event) => setRequestReason(event.currentTarget.value)}
                            styles={{
                                root: { display: isRequestingAccess ? "block" : "none" },
                                label: { fontSize: "16px" },
                                input: { fontSize: "16px" },
                            }}
                        />
                    </Stack>

                    <Group justify="flex-end" mt={24}>
                        <Button
                            variant="default"
                            disabled={loading}
                            onClick={onClose ?? (() => navigate({ to: source ?? "/" }))}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={
                                isRequestingAccess ? handleRequestAccess : handleSavePreferences
                            }
                            disabled={
                                loading ||
                                !selectedProductLine ||
                                (isRequestingAccess && !requestReason.trim()) ||
                                (isRequestingAccess
                                    ? Object.values(dataSourceSelections).every(
                                          (selected) => !selected,
                                      )
                                    : !useAllDatasources &&
                                      Object.values(dataSourceSelections).every(
                                          (selected) => !selected,
                                      ))
                            }
                            loading={loading}
                            style={(theme) => ({
                                "&:disabled": {
                                    backgroundColor: theme.colors.gray[2],
                                    color: theme.colors.gray[5],
                                },
                            })}
                        >
                            {loading ? "Submitting..." : displayButtonText}
                        </Button>
                    </Group>
                </div>
            </Container>
        </main>
    );
}

export const Route = createFileRoute("/_layout-nosidebar/questionnaire")({
    component: Questionnaire,
});
export default Questionnaire;
